import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef } from "react";
import {
  View,
  Text,
  StyleSheet,
  Alert,
  SafeAreaView,
  TouchableOpacity,
  ScrollView,
  Platform,
  KeyboardAvoidingView,
  TextInput,
  Image
} from "react-native";
import { useTranslation } from "react-i18next";
import type { TextInput as RNTextInput } from "react-native";
import { consumerLookup } from "../../../../services/send.service";
import { SessionState, useSessionStore } from "../../../../../../state/src/useSessionStore";
import EnglishFlag from "../../../../../../../assets/images/english-flag.png";
import  FloatingTextInputComponent from "../../common/FloatingTextInputComponent";
import Colors from "../../../../constants/colors";
import { ComponentStyles } from "../../../../constants/componentStyles";


type Props = {
  onValidationChange?: (valid: boolean) => void;
};

export type EnterPhoneStepHandle = {
  submit: () => Promise<'ok' | 'notfound'>;
};

const EnterPhoneStep = forwardRef<EnterPhoneStepHandle, Props>(({ onValidationChange }: Props, ref) => {
  const { t } = useTranslation();
  const agentId = useSessionStore((s: SessionState) => s.agentId);
  const unitProfileId = useSessionStore((s: SessionState) => s.unitProfileId);
  const setPhoneNumberStore = useSessionStore((s: SessionState) => (s as any).setPhoneNumber);
  const setIsNewConsumer = useSessionStore((s: SessionState) => (s as any).setIsNewConsumer);
  const [phoneNumber, setPhoneNumber] = useState("");
  const inputRef = useRef<RNTextInput | null>(null);

  // Focus input automatically when component mounts (ensures mobile keyboard shows)
  useEffect(() => {
    const id = setTimeout(() => inputRef.current?.focus(), 100);
    return () => clearTimeout(id);
  }, []);

  const formatPhoneNumber = (number: string) => {
    const cleaned = number.replace(/\D/g, "");
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6) return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  };

  const submit = async (): Promise<'ok' | 'notfound'> => {
    if (phoneNumber.length !== 10) {
      Alert.alert("Error", t("PHONE_STEP_ERROR_INVALID"));
      throw new Error('Invalid phone number');
    }
    if (!agentId || !unitProfileId) {
      Alert.alert("Error", t("PHONE_STEP_ERROR_MISSING_LOCATION"));
      throw new Error('Missing agent or unit profile');
    }
    try {
      const ConsumerLookupInfo = await consumerLookup(phoneNumber, agentId, unitProfileId);
      console.log("consumerLookup Response:", ConsumerLookupInfo);
      try { setIsNewConsumer?.(false); } catch {}
      return 'ok';
    } catch (error: any) {
      const code = error?.code || error?.ErrorDetail?.ErrorCode;
      if (String(code) === "616") {
        try { setIsNewConsumer?.(true); } catch {}
        return 'notfound';
      }
      try { setIsNewConsumer?.(false); } catch {}
      Alert.alert("Error", t("PHONE_STEP_ERROR_FAILED"));
      throw error;
    } finally {
      console.log('')
    }
  };

  useImperativeHandle(ref, () => ({ submit }));

  const handlePhoneChange = (text: string) => {
    const cleaned = text.replace(/\D/g, "");
    if (cleaned?.length <= 10) {
      setPhoneNumber(cleaned);
      try { setPhoneNumberStore?.(cleaned.length === 10 ? cleaned : null); } catch {}
      // Tell parent if valid
      onValidationChange?.(cleaned?.length === 10);
    }
  };

  const Wrapper = Platform.OS === "web" ? View : SafeAreaView;
  return (
    <Wrapper style={styles.container}>
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
      >
        <View>
          <ScrollView
            contentContainerStyle={[styles.scrollContainer, { flexGrow: 1 }]}
            keyboardShouldPersistTaps="handled"
            keyboardDismissMode="on-drag"
          >
            <View>
              <Text style={styles.title}>{t("PHONE_STEP_TITLE")}</Text>
              <Text style={styles.subtitle}>{t("PHONE_STEP_SUBTITLE")}</Text>
              <View style={styles.phoneInputContainer}>
                <View style={styles.countryCodeSelector}>
                  <Image
                    source={EnglishFlag}
                    style={{height: 24, width: 24, borderRadius: 50, marginVertical: 6, marginRight: 8}}
                    resizeMode='contain'           
                  />
                  <Text style={styles.plusText}>+1</Text>
                </View>
                <FloatingTextInputComponent testID="phone-input"
                  label={t("PHONE_STEP_PLACEHOLDER")}
                  value={formatPhoneNumber(phoneNumber)}
                  onChangeText={handlePhoneChange}
                 />
              </View>
            </View>
          </ScrollView>
        </View>
      </KeyboardAvoidingView>
    </Wrapper>
  );
});

export default EnterPhoneStep;

const styles = StyleSheet.create({
  container: {
    backgroundColor: Colors.white
  },
  scrollContainer: {
    paddingHorizontal: 24,
    paddingBottom: 40,
    alignItems: "stretch",
    justifyContent: "flex-start"
  },
  title: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.titleFont,
    fontWeight: '500', 
    color: Colors.titleTextColor,
    lineHeight: 34,
    marginBottom: 8
  },
  subtitle: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: '400',
    lineHeight: 24,
    color: Colors.subTitleColor,
    marginBottom: 32
  },
  phoneInputContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 20,
  },
  countryCodeSelector: {
    height: 56,
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#F3F4F6",
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 6,
    marginRight: 8,
    // borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  countryText: {
    fontSize: 14,
    fontWeight: "600",
    color: "#111827",
    marginRight: 4,
  },
  plusText: {
    fontSize: 16,
    fontFamily: 'Rubik',
    fontWeight: "400",
    lineHeight: 24,
    color: Colors.titleTextColor,
  },
  textInput: {
    width: 295,
    height: 56,
    backgroundColor: Colors.textInputBackground,
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: Platform.OS === "ios" ? 12 : 6,
    fontSize: 16,
    fontWeight: "400",
    lineHeight: 24,
    color: "#111827",
  },
});